import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { BarChart3, BookOpen, GraduationCap, Users, CheckCircle2, XCircle, Award, TrendingUp, UserX, Layers } from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { StatCard } from '../../../shared/components/cards/StatCard';
import type { ComparisonAttribute } from '../types';
import {
  useGetResultSummaryQuery,
  useGetResultRegionWiseQuery,
  useGetResultGenderWiseQuery,
  useGetResultCategoryWiseQuery,
  useGetResultSponsoredWiseQuery,
  useGetResultPmShriWiseQuery,
  useGetResultUrbanRuralWiseQuery,
  useGetResultStreamWiseQuery,
  useGetResultCwsnWiseQuery,
} from '../services/api';
import RegionWiseTable from '../components/RegionWiseTable';
import ComparisonAnalysisPanel from '../components/ComparisonAnalysisPanel';
import ResultYearDropdown from '../components/ResultYearDropdown';

const tabs = [
  { id: 'class10', label: 'Class 10', icon: BookOpen },
  { id: 'class12', label: 'Class 12', icon: GraduationCap },
] as const;
type TabKey = typeof tabs[number]['id'];

const COMPARISON_TABS_CLASS10: ComparisonAttribute[] = ['overall', 'sponsoredBy', 'gender', 'category', 'urbanRural', 'cwsn', 'pmShri'];

interface RegionChartPoint {
  regionCode: string;
  totalStudents: number;
  passPercentage: number;
  performanceIndex: number;
}

interface DivergingChartProps {
  data: RegionChartPoint[];
  metric: 'passPercentage' | 'performanceIndex';
  title: string;
  positiveColor: string;
  negativeColor: string;
  unit?: string;
}

const RegionDivergingChart: React.FC<DivergingChartProps> = ({
  data, metric, title, positiveColor, negativeColor, unit = '%',
}) => {
  const avg = useMemo(
    () => (data.length ? +(data.reduce((s, d) => s + d[metric], 0) / data.length).toFixed(2) : 0),
    [data, metric],
  );

  const chartData = useMemo(
    () =>
      [...data]
        .sort((a, b) => b[metric] - a[metric])
        .map((item) => ({
          region: item.regionCode,
          value: +(item[metric] - avg).toFixed(2),
          actual: item[metric],
        })),
    [data, metric, avg],
  );

  const chartMinWidth = Math.max(600, chartData.length * 40);

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-1 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
          {metric === 'performanceIndex' ? 'PI' : 'Pass%'}: {avg}{unit}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-4">Deviation from national average — above avg: positive, below avg: negative</p>
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: `${chartMinWidth}px` }}>
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={chartData} margin={{ top: 28, right: 16, left: 8, bottom: 72 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="region"
                tick={{ fontSize: 13, fill: '#667085' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={0}
                angle={-35}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tick={{ fontSize: 13, fill: '#667085' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `${+(v + avg).toFixed(1)}${unit}`}
                domain={[(devMin: number) => +(devMin - Math.max(0.5, Math.abs(devMin) * 0.15)).toFixed(2), (devMax: number) => +(devMax + Math.max(0.5, Math.abs(devMax) * 0.15)).toFixed(2)]}
              />
              <ReferenceLine
                y={0}
                stroke="#374151"
                strokeWidth={1.5}
                label={{ value: `Avg ${avg}${unit}`, position: 'insideTopRight', fontSize: 13, fill: '#374151' }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
                      <p className="font-semibold text-gray-800 mb-1">{label}</p>
                      <p className="text-gray-600">Actual: <span className="font-bold">{p.actual}{unit}</span></p>
                      <p style={{ color: p.value >= 0 ? positiveColor : negativeColor }}>
                        Deviation: <span className="font-bold">{p.value >= 0 ? '+' : ''}{p.value}{unit}</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={18}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.value >= 0 ? positiveColor : negativeColor}
                    fillOpacity={0.85}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  content={(props: any) => {
                    const { x, y, width, height, value: dev } = props;
                    const actual = +(dev + avg).toFixed(1);
                    const labelY = dev >= 0 ? (y as number) - 5 : (y as number) + (height as number) + 13;
                    return (
                      <text x={(x as number) + (width as number) / 2} y={labelY} textAnchor="middle" fontSize={9} fill="#374151" fontWeight={600}>
                        {actual}{unit}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default function ResultDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('class10');
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const grade = activeTab === 'class10' ? 10 : 12;
  const { data: resultSummary, isFetching: isSummaryLoading } = useGetResultSummaryQuery({ grade, year: selectedYear });
  const { data: regionwiseData, isFetching: isRegionwiseLoading } = useGetResultRegionWiseQuery({ grade, year: selectedYear });
  const { data: genderwiseData, isFetching: isGenderwiseLoading } = useGetResultGenderWiseQuery({ grade, year: selectedYear });
  const { data: categorywiseData, isFetching: isCategorywiseLoading } = useGetResultCategoryWiseQuery({ grade, year: selectedYear });
  const { data: sponsoredwiseData, isFetching: isSponsoredwiseLoading } = useGetResultSponsoredWiseQuery({ grade, year: selectedYear });
  const { data: pmshriwiseData, isFetching: isPmShriWiseLoading } = useGetResultPmShriWiseQuery({ grade, year: selectedYear });
  const { data: urbanruralwiseData, isFetching: isUrbanRuralWiseLoading } = useGetResultUrbanRuralWiseQuery({ grade, year: selectedYear });
  const { data: streamwiseData, isFetching: isStreamWiseLoading } = useGetResultStreamWiseQuery(
    grade === 12 ? { grade, year: selectedYear } : skipToken,
  );
  const { data: cwsnwiseData, isFetching: isCwsnWiseLoading } = useGetResultCwsnWiseQuery({ grade, year: selectedYear });

  const normalizedGenderSummary = useMemo(() => {
    return {
      maleCount: genderwiseData?.find((g) => g.gender.toLowerCase() === 'male')?.totalStudents ?? 0,
      femaleCount: genderwiseData?.find((g) => g.gender.toLowerCase() === 'female')?.totalStudents ?? 0,
      malePassRate: genderwiseData?.find((g) => g.gender.toLowerCase() === 'male')?.passPercentage ?? 0,
      femalePassRate: genderwiseData?.find((g) => g.gender.toLowerCase() === 'female')?.passPercentage ?? 0,
      malePerformanceIndex: genderwiseData?.find((g) => g.gender.toLowerCase() === 'male')?.performanceIndex ?? 0,
      femalePerformanceIndex: genderwiseData?.find((g) => g.gender.toLowerCase() === 'female')?.performanceIndex ?? 0,
    };
  }, [genderwiseData]);

  const summary = useMemo(() => {

    return {
      totalStudents: resultSummary?.totalStudents || 0,
      passed: resultSummary?.totalPassed || 0,
      essentialRepeat: resultSummary?.totalEssentialRepeat || 0,
      absent: resultSummary?.totalAbsent || 0,
      comptt: resultSummary?.totalCompartment || 0,
      passPercentage: resultSummary?.passPercentage || 0,
      performanceIndex: resultSummary?.performanceIndex || 0,
      maleCount: normalizedGenderSummary.maleCount,
      femaleCount: normalizedGenderSummary.femaleCount,
      malePassRate: normalizedGenderSummary.malePassRate,
      femalePassRate: normalizedGenderSummary.femalePassRate,
      malePerformanceIndex: normalizedGenderSummary.malePerformanceIndex,
      femalePerformanceIndex: normalizedGenderSummary.femalePerformanceIndex,
    };
  }, [normalizedGenderSummary, resultSummary]);

  const apiComparisonData = useMemo(() => {
    const overall = {
      groupName: 'Overall',
      totalStudents: summary.totalStudents,
      passed: summary.passed,
      passPercentage: summary.passPercentage,
      performanceIndex: summary.performanceIndex,
    };

    const gender = (genderwiseData || []).map((item) => ({
      groupName: item.gender,
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const category = (categorywiseData || []).map((item) => ({
      groupName: item.socialCategory,
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const sponsoredBy = (sponsoredwiseData || []).map((item) => ({
      groupName: item.sector,
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const pmShri = (pmshriwiseData || []).map((item) => ({
      groupName: item.label || ((item.isPmShri ?? item.isPmshri) ? 'PM SHRI' : 'Non-PM SHRI'),
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const urbanRural = (urbanruralwiseData || []).map((item) => ({
      groupName: item.locationType,
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const stream = (streamwiseData || []).map((item) => ({
      groupName: item.streamLabel || item.streamName || item.label || item.stream,
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    const cwsn = (cwsnwiseData || []).map((item) => ({
      groupName: item.label || ((item.isCWSN ?? item.isCwsn ?? item.cwsn) ? 'CWSN' : 'Non-CWSN'),
      totalStudents: item.totalStudents,
      passed: item.passed,
      passPercentage: item.passPercentage,
      performanceIndex: item.performanceIndex,
    }));

    return { overall, sponsoredBy, gender, category, urbanRural, cwsn, pmShri, stream };
  }, [summary, sponsoredwiseData, genderwiseData, categorywiseData, urbanruralwiseData, cwsnwiseData, pmshriwiseData, streamwiseData]);

  const regionCards = useMemo(() => {
    return (regionwiseData || []).map((region) => ({
      regionCode: region.region,
      state: region.region,
      totalStudents: region.totalRegistered,
      passPercentage: region.passPercentage,
      performanceIndex: region.performanceIndex,
    }));
  }, [regionwiseData]);

  const kpiCards = [
    { title: 'Total Students', value: summary.totalStudents, subtitle: `Class ${grade} appeared`, icon: <Users className="w-5 h-5" />, color: 'purple' as const },
    { title: 'Pass %', value: `${summary.passPercentage}%`, subtitle: `${summary.passed} passed`, icon: <CheckCircle2 className="w-5 h-5" />, color: 'green' as const },
    { title: 'Compartment', value: summary.comptt, subtitle: `${summary.totalStudents > 0 ? ((summary.comptt / summary.totalStudents) * 100).toFixed(1) : 0}%`, icon: <BookOpen className="w-5 h-5" />, color: 'orange' as const },
    { title: 'Absent', value: `${summary.absent ?? 0}`, icon: <UserX className="w-5 h-5" />, color: 'blue' as const },
    { title: 'Ess. Repeat', value: summary.essentialRepeat, subtitle: `${summary.totalStudents > 0 ? ((summary.essentialRepeat / summary.totalStudents) * 100).toFixed(1) : 0}%`, icon: <XCircle className="w-5 h-5" />, color: 'red' as const },
    { title: 'Performance Index', value: `${summary.performanceIndex ?? 0}%`, icon: <TrendingUp className="w-5 h-5" />, color: 'pink' as const },
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header + Tab */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F36B21] flex items-center gap-2">
            <BarChart3 className="w-7 h-7" />
            Result Analysis Dashboard
          </h1>
          <p className="text-gray-600 text-sm mt-1">CBSE Board Examination Performance — National Overview</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="bg-white rounded-xl border border-gray-200 inline-flex overflow-hidden">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-[#F36B21] border-[#F36B21] bg-orange-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <ResultYearDropdown
            grade={grade}
            value={selectedYear}
            onChange={setSelectedYear}
          />

          <button
            onClick={() => navigate('classification')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#F36B21] text-white text-sm font-semibold hover:bg-orange-600 transition-colors shadow-sm whitespace-nowrap"
          >
            <Layers className="w-4 h-4" />
            Classification Analysis
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>
          {isSummaryLoading ||
          isRegionwiseLoading ||
          isGenderwiseLoading ||
          isCategorywiseLoading ||
          isSponsoredwiseLoading ||
          isPmShriWiseLoading ||
          isUrbanRuralWiseLoading ||
          isCwsnWiseLoading ||
          isStreamWiseLoading
            ? 'Loading result data...'
            : `Showing ${grade}th class for ${selectedYear}`}
        </span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((c) => (
          <StatCard key={c.title} title={c.title} value={c.value} subtitle={c.subtitle} icon={c.icon} color={c.color} />
        ))}
      </div>

      {/* Comparison Analysis — Overall */}
      <ComparisonAnalysisPanel
        students={[]}
        title="Comparison Analysis — Overall"
        apiComparison={apiComparisonData}
        availableTabs={grade === 10 ? COMPARISON_TABS_CLASS10 : undefined}
      />
      {/* Region-wise Diverging Charts */}
        <RegionDivergingChart
          data={regionCards}
          metric="performanceIndex"
          title="Performance Index — Regional Deviation"
          positiveColor="#10b981"
          negativeColor="#f43f5e"
        />
        <RegionDivergingChart
          data={regionCards}
          metric="passPercentage"
          title="Pass Percentage — Regional Deviation"
          positiveColor="#3b82f6"
          negativeColor="#f97316"
        />


      {/* Region Drill-Down Table */}
      <RegionWiseTable data={regionwiseData ?? []} activeTab={activeTab} year={selectedYear} />
    </div>
  );
}
